import React, { useEffect, useRef, useState } from "react";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import { Menu, MenuItem, MenuItems, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  DocumentDownloadIcon,
  DocumentIcon,
} from "@heroicons/react/outline";
import {
  clearExportBalance,
  clearExportBalanceSheet,
  clearExportCapitalStatement,
  clearExportCashflow,
  clearExportGeneralJournal,
  clearExportIncomeStatement,
  requestExportAccountBalance,
  requestExportBalanceSheet,
  requestExportCapitalStatement,
  requestExportCashflow,
  requestExportGeneralJournal,
  requestExportIncomeStatement,
  getAging,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../Context";
import { useTranslation } from "react-i18next";
import { PARTNER_REPORT_OPTIONS, REPORT_OPTIONS } from "../../../../Constants";
import {
  cancelAPIRequest,
  cancelStorageRequest,
  getAccountingFileName,
} from "../../../../Utils";
import { downloadData, getUrl, isCancelError } from "@aws-amplify/storage";
import dayjs from "dayjs";
import {
  wibEndOfDay,
  wibThisMonth,
  wibToday,
} from "../../../../Utils/PeriodUtils";
import { useNavigate } from "react-router-dom";
import { Modal } from "../../../../Components/Modal";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const V2_REPORT_ROUTES = {
  "general-journal": "/v2/accounting/reports/general-journal",
  "balance-sheet": "/v2/accounting/reports/balance-sheet",
  "trial-balance": "/v2/accounting/reports/trial-balance",
  "income-statement": "/v2/accounting/reports/income-statement",
  "capital-statement": "/v2/accounting/reports/capital-statement",
  "cash-flow": "/v2/accounting/reports/cash-flow",
  aging: "/v2/accounting/reports/aging",
  "partner-ledger": "/v2/accounting/reports/partner-ledger",
};

const AccountingReports = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthState();
  const navigate = useNavigate();

  const [showExportError, setshowExportError] = useState(false);
  const [downloading, setdownloading] = useState(false);
  const [showDownloadErrorModal, setshowDownloadErrorModal] = useState(false);
  const [downloadProgress, setdownloadProgress] = useState(0);
  const [downloadDocError, setdownloadDocError] = useState(null);
  const [activeMenuExport, setActiveMenuExport] = useState(null);
  // getAging is called directly, not through the accounting slice, so its
  // loading state is tracked here.
  const [agingExportLoading, setAgingExportLoading] = useState(false);
  const downloadRef = useRef(null);

  const accountingDispatch = useAccountingDispatch();
  const {
    request,
    generalJournalFile,
    generalJournalFileLoading,
    generalJournalFileError,
    balanceSheetExport,
    balanceSheetExportLoading,
    balanceSheetExportError,
    balanceExportFile,
    balanceExportLoading,
    balanceExportError,
    incomeExportFile,
    incomeExportLoading,
    incomeExportError,
    capitalExportFile,
    capitalExportLoading,
    capitalExportError,
    cashFlowExportFile,
    cashFlowExportLoading,
    cashFlowExportError,
  } = useAccountingState();

  const exportFileError = [
    generalJournalFileError,
    balanceSheetExportError,
    balanceExportError,
    incomeExportError,
    capitalExportError,
    cashFlowExportError,
  ].find((error) => error);

  const isExportLoading = [
    generalJournalFileLoading,
    balanceSheetExportLoading,
    balanceExportLoading,
    incomeExportLoading,
    capitalExportLoading,
    cashFlowExportLoading,
    agingExportLoading,
  ].some((status) => status);

  // Clear unmount
  useEffect(() => {
    return () => {
      if (downloadRef.current) {
        cancelStorageRequest(downloadRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!exportFileError) {
      setshowExportError(false);
    } else {
      setshowExportError(true);
    }
  }, [exportFileError]);

  useEffect(() => {
    const exportFiles = [
      generalJournalFile,
      balanceSheetExport,
      balanceExportFile,
      incomeExportFile,
      capitalExportFile,
      cashFlowExportFile,
    ];

    const fileKey = exportFiles.find((file) => file?.key)?.key;

    if (fileKey) {
      downloadFile(fileKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    generalJournalFile,
    balanceSheetExport,
    balanceExportFile,
    incomeExportFile,
    capitalExportFile,
    cashFlowExportFile,
  ]);

  useEffect(() => {
    if (activeMenuExport === null) {
      cancelAPIRequest(request);
      if (downloadRef.current) {
        cancelStorageRequest(downloadRef.current);
        downloadRef.current.isCancelled = true;
      }
    }
  }, [activeMenuExport, request]);

  const clearAllExports = (dispatch) => {
    clearExportGeneralJournal(dispatch);
    clearExportBalance(dispatch);
    clearExportBalanceSheet(dispatch);
    clearExportIncomeStatement(dispatch);
    clearExportCapitalStatement(dispatch);
    clearExportCashflow(dispatch);
  };

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setdownloadProgress(0);
    } else {
      setdownloadProgress(transferredBytes / totalBytes);
    }
  };

  const downloadFile = async (key) => {
    try {
      setdownloading(true);

      const request = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          onProgress: onDownloadProgress,
        },
      });

      downloadRef.current = request;
      const result = await request.result;
      const fileName = getAccountingFileName(key);

      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (isCancelError(downloadError)) {
        // Handle error thrown by task cancellation.
      } else {
        await handleDownloadError(key, downloadError);
      }
    } finally {
      setdownloading(false);
      setdownloadProgress(0);
      clearAllExports(accountingDispatch);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const handleDownloadError = async (key, downloadError) => {
    if (downloadRef.current?.isCancelled) {
      return;
    }
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          expiresIn: 3600,
          useAccelerateEndpoint: true,
        },
      });

      const signedURL = urlResult.url.href;
      window.open(signedURL, "_blank", "noopener,noreferrer");
    } catch (signedURLError) {
      setdownloadDocError(signedURLError);
      setshowDownloadErrorModal(true);
    }
  };

  const handleDownloadFile = (reportID, exportType) => (event) => {
    event.preventDefault();

    const reportActions = {
      "general-journal": () =>
        requestExportGeneralJournal(accountingDispatch, {
          businessID: user.mainBusiness?.id,
          ...wibThisMonth(),
          exportType,
        }),
      "balance-sheet": () =>
        requestExportBalanceSheet(accountingDispatch, {
          businessID: user.mainBusiness?.id,
          date: wibEndOfDay(wibToday()),
          exportType,
        }),
      "trial-balance": () =>
        requestExportAccountBalance(accountingDispatch, {
          businessID: user.mainBusiness?.id,
          ...wibThisMonth(),
          exportType,
        }),
      "income-statement": () =>
        requestExportIncomeStatement(accountingDispatch, {
          businessID: user.mainBusiness?.id,
          ...wibThisMonth(),
          exportType,
        }),
      "capital-statement": () =>
        requestExportCapitalStatement(accountingDispatch, {
          businessID: user.mainBusiness?.id,
          date: wibEndOfDay(wibToday()),
          exportType,
        }),
      "cash-flow": () =>
        requestExportCashflow(accountingDispatch, {
          businessID: user.mainBusiness?.id,
          ...wibThisMonth(),
          exportType,
        }),
      // Receivables as of today. Fetched here rather than through the shared
      // accounting slice, so the file is downloaded straight from the reply.
      aging: () => {
        setAgingExportLoading(true);
        return getAging({
          businessID: user.mainBusiness?.id,
          type: "receivable",
          date: wibEndOfDay(wibToday()),
          exportType,
        })
          .then((file) => file?.key && downloadFile(file.key))
          .catch((error) => {
            setdownloadDocError(error);
            setshowDownloadErrorModal(true);
          })
          .finally(() => setAgingExportLoading(false));
      },
    };

    reportActions[reportID]?.();
  };

  const renderDownloadError = () => {
    if (downloadDocError) {
      return (
        <ErrorDialog
          open={showDownloadErrorModal}
          error={downloadDocError}
          onClose={() => {
            setshowDownloadErrorModal(false);
            setdownloadDocError(null);
          }}
        />
      );
    }
    return null;
  };

  const renderFileModal = () => (
    <Modal
      title="Download File"
      show={downloading}
      onClose={() => {
        if (downloadRef.current) {
          const downloadTask = downloadRef.current;
          downloadTask.cancel();
        }
        setdownloading(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <div className="flex-1 overflow-hidden">
            <div className="w-full">
              <p className="text-xs text-slate-500">{t("Downloading")}</p>
              <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
                  style={{
                    width: `${Math.round(downloadProgress * 100)}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex justify-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
        </div>
      </div>
    </Modal>
  );

  const renderExportError = () => {
    if (exportFileError) {
      return (
        <ErrorDialog
          open={showExportError}
          error={exportFileError}
          onClose={() => {
            setshowExportError(false);
            clearAllExports(accountingDispatch);
          }}
        />
      );
    }
    return null;
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        {/* Page header */}
        <AccountingPageHeader title="accounting.reports.label" />

        {/* Report List */}
        <div className="px-4 py-5 sm:px-6">
          {activeMenuExport !== null && (
            <div
              className="fixed inset-0 z-[5]"
              onClick={() => setActiveMenuExport(null)}
            />
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ...REPORT_OPTIONS.filter(
                (report) => report.id !== "accounting-summary",
              ),
              ...PARTNER_REPORT_OPTIONS,
            ].map((report, index) => {
              // Every report has a v2 page; the fallback to the old report
              // pages is gone.
              const v2Path = V2_REPORT_ROUTES[report.id];

              return (
                <a
                  key={report.id}
                  href={v2Path}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(v2Path);
                  }}
                  className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm hover:border-gray-400 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 cursor-pointer"
                >
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      {t(report.name)}
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {t(report.description)}
                    </p>
                  </div>
                  {report.download !== false && (
                    // The menu sits inside the card's link. Stop every click in
                    // it here (button, period label, padding, divider) so none
                    // of them follows the href or opens the report page.
                    <div
                      className="relative inline-block text-left mt-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <Menu as="div" className="relative">
                        <button
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium border transition-colors cursor-pointer ${
                            activeMenuExport === index
                              ? "bg-blue-50 border-blue-300 text-blue-600"
                              : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-700"
                          }`}
                          onClick={() => {
                            setActiveMenuExport((prevIndex) =>
                              prevIndex === index ? null : index,
                            );
                          }}
                        >
                          <span>{t("reports.actions.download")}</span>
                          <ChevronDownIcon
                            className={`h-3.5 w-3.5 transition-transform ${activeMenuExport === index ? "rotate-180" : ""}`}
                          />
                        </button>
                        <Transition
                          show={activeMenuExport === index}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <MenuItems className="absolute lg:right-0 z-20 max-[1080px]:-top-24 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="py-1">
                              <div className="px-4 py-2 border-b border-gray-100">
                                <p className="text-xs text-gray-400">
                                  {[
                                    "balance-sheet",
                                    "capital-statement",
                                    "aging",
                                  ].includes(report.id)
                                    ? `${t("reports.actions.period.asOf")} ${dayjs().locale(i18n.language).format("MMMM YYYY")}`
                                    : `${t("reports.actions.period.range")} ${dayjs().locale(i18n.language).format("MMMM YYYY")}`}
                                </p>
                              </div>
                              <div>
                                <MenuItem>
                                  {({ focus }) => (
                                    <button
                                      disabled={isExportLoading}
                                      onClick={handleDownloadFile(
                                        report?.id,
                                        "pdf",
                                      )}
                                      className={`${
                                        focus ? "bg-gray-100" : ""
                                      } flex w-full px-4 py-2 text-sm text-gray-700 items-center hover:bg-gray-50 ${
                                        isExportLoading
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }`}
                                    >
                                      {isExportLoading ? (
                                        <span className="mr-3">
                                          <svg
                                            className="animate-spin h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                          >
                                            <circle
                                              className="opacity-25"
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              stroke="currentColor"
                                              strokeWidth="4"
                                            ></circle>
                                            <path
                                              className="opacity-75"
                                              fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                          </svg>
                                        </span>
                                      ) : (
                                        <DocumentDownloadIcon className="h-5 w-5 mr-3 text-gray-400" />
                                      )}
                                      {t("reports.actions.downloads.pdf")}
                                    </button>
                                  )}
                                </MenuItem>
                                <MenuItem>
                                  {({ focus }) => (
                                    <button
                                      disabled={isExportLoading}
                                      onClick={handleDownloadFile(
                                        report?.id,
                                        "xlsx",
                                      )}
                                      className={`${
                                        focus ? "bg-gray-100" : ""
                                      } flex w-full px-4 py-2 text-sm text-gray-700 items-center hover:bg-gray-50 ${
                                        isExportLoading
                                          ? "opacity-50 cursor-not-allowed"
                                          : ""
                                      }`}
                                    >
                                      {isExportLoading ? (
                                        <span className="mr-3">
                                          <svg
                                            className="animate-spin h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                          >
                                            <circle
                                              className="opacity-25"
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              stroke="currentColor"
                                              strokeWidth="4"
                                            ></circle>
                                            <path
                                              className="opacity-75"
                                              fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                          </svg>
                                        </span>
                                      ) : (
                                        <DocumentDownloadIcon className="h-5 w-5 mr-3 text-gray-400" />
                                      )}
                                      {t("reports.actions.downloads.excel")}
                                    </button>
                                  )}
                                </MenuItem>
                              </div>
                            </div>
                          </MenuItems>
                        </Transition>
                      </Menu>
                    </div>
                  )}
                </a>
              );
            })}
          </div>
        </div>

        {renderFileModal()}
        {renderDownloadError()}
        {renderExportError()}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingReports;
